import React, { useContext, useEffect, useState } from "react"
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  List,
  ListItem,
  Radio,
  Switch,
  Typography,
  useTheme
} from "@mui/material"
import { useFormikContext } from "formik"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons"
import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { AppContext } from "components/AppContextProvider"
import { IDocumentFolder } from "pages/documents/interface/folder.interface"
import { StyledListItem } from "shared/styled/General"
import {
  StyleAccordion,
  StyleAccordionDetails,
  StyleAccordionSummary,
  StyledBoxWrapper,
  StyledFormControlLabelBold,
  StyledListItemWrapper
} from "components/PermissionAndRoles/PermissionAndRolesSelect.styled"
import { useTranslation } from "react-i18next"
import InfoTooltip from "components/Tooltip/InfoTooltip"

interface Department {
  id: number
  name: string
  selected: boolean
}

interface Company {
  id: number
  name: string
  selected: boolean
  departments: Department[]
}

export const CompanyAndDepartmentSelector = () => {
  const appContext = useContext(AppContext)
  const { values, setFieldValue } = useFormikContext<IDocumentFolder>()
  const [specificCompanyIds, setSpecificCompanyIds] = useState<number[]>([])
  const customerCompanies = appContext.appUser.customer.companies
  const allCompanyIds = customerCompanies.map((company: Company) => company.id)
  const { t } = useTranslation()
  const theme = useTheme()

  const availabilityStatus = values.isAvailableForAll ? (
    <Chip
      color="success"
      label={t("documents.all_companies_and_departments", "All companies and departments")}
      size="small"
    />
  ) : !values.isAvailableForAll && values.companyIds.length > 0 ? (
    <Chip
      color="info"
      label={t("documents.availability.limited", "Specifika bolag och avdelningar")}
      size="small"
    />
  ) : (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Chip color="default" label={t("documents.document_inactive", "Document inactive")} size="small" />
      <InfoTooltip
        key="inactive-information-tooltip"
        title={t(
          "documents.inactive_information",
          "The document is inactive because no companies or departments have been selected for access."
        )}
      />
    </Box>
  )

  useEffect(() => {
    const selectedCompanyIds: number[] = values.companyIds || []
    const selectedDepartmentIds: number[] = values.departmentIds || []

    const companiesWithSpecificDepartments = customerCompanies
      .filter(
        (company: Company) =>
          selectedCompanyIds.includes(company.id) &&
          company.departments.some((department) => selectedDepartmentIds.includes(department.id))
      )
      .map((company: Company) => company.id)

    setSpecificCompanyIds(companiesWithSpecificDepartments)
  }, [values.companyIds, values.departmentIds, customerCompanies])

  const handleAllCompaniesChange = (checked: boolean) => {
    if (checked) {
      setFieldValue("isAvailableForAll", true)
      setFieldValue("companyIds", [])
      setFieldValue("departmentIds", [])
      setSpecificCompanyIds([])
    } else {
      setFieldValue("isAvailableForAll", false)
    }
  }

  const maybeCollapseToAvailableForAll = (companyIds: number[], departmentIds: number[]) => {
    if (companyIds.length === allCompanyIds.length && departmentIds.length === 0) {
      setFieldValue("isAvailableForAll", true)
      setFieldValue("companyIds", [])
      setFieldValue("departmentIds", [])
      setSpecificCompanyIds([])
      return true
    }
    return false
  }

  const handleCompanyDepartmentSelection = (
    company: Company,
    selectionType: "all" | "specific",
    departmentId?: number
  ) => {
    const wasAvailableForAll = values.isAvailableForAll
    const selectedCompanyIds: number[] = wasAvailableForAll ? allCompanyIds : values.companyIds || []
    const selectedDepartmentIds: number[] = wasAvailableForAll ? [] : values.departmentIds || []

    if (selectionType === "all") {
      const otherCompanyDepartments = selectedDepartmentIds.filter(
        (deptId) => !company.departments.some((d) => d.id === deptId)
      )
      const newCompanyIds = selectedCompanyIds.includes(company.id)
        ? selectedCompanyIds
        : [...selectedCompanyIds, company.id]

      setSpecificCompanyIds((previous) => previous.filter((id) => id !== company.id))

      if (!maybeCollapseToAvailableForAll(newCompanyIds, otherCompanyDepartments)) {
        setFieldValue("companyIds", newCompanyIds)
        setFieldValue("departmentIds", otherCompanyDepartments)
        setFieldValue("isAvailableForAll", false)
      }
    } else if (selectionType === "specific" && departmentId) {
      const isDepartmentSelected = selectedDepartmentIds.includes(departmentId)

      if (isDepartmentSelected) {
        const newDepartmentIds = selectedDepartmentIds.filter((id) => id !== departmentId)

        const remainingDepartmentsForCompany = newDepartmentIds.filter((deptId) =>
          company.departments.some((d) => d.id === deptId)
        )

        const newCompanyIds =
          remainingDepartmentsForCompany.length === 0
            ? selectedCompanyIds.filter((id) => id !== company.id)
            : selectedCompanyIds

        if (remainingDepartmentsForCompany.length === 0) {
          setSpecificCompanyIds((previous) => previous.filter((id) => id !== company.id))
        }

        if (!maybeCollapseToAvailableForAll(newCompanyIds, newDepartmentIds)) {
          setFieldValue("companyIds", newCompanyIds)
          setFieldValue("departmentIds", newDepartmentIds)
          setFieldValue("isAvailableForAll", false)
        }
      } else {
        const newDepartmentIds = [...selectedDepartmentIds, departmentId]
        const newCompanyIds = selectedCompanyIds.includes(company.id)
          ? selectedCompanyIds
          : [...selectedCompanyIds, company.id]

        setSpecificCompanyIds((previous) =>
          previous.includes(company.id) ? previous : [...previous, company.id]
        )

        if (!maybeCollapseToAvailableForAll(newCompanyIds, newDepartmentIds)) {
          setFieldValue("companyIds", newCompanyIds)
          setFieldValue("departmentIds", newDepartmentIds)
          setFieldValue("isAvailableForAll", false)
        }
      }
    }
  }

  const getCompanySelectionType = (company: Company): "none" | "all" | "specific" => {
    if (values.isAvailableForAll) {
      return "all"
    }

    const selectedCompanyIds: number[] = values.companyIds || []
    const selectedDepartmentIds: number[] = values.departmentIds || []

    if (!selectedCompanyIds.includes(company.id)) {
      return "none"
    }

    const selectedDepartmentsForCompany = company.departments.filter((dept) =>
      selectedDepartmentIds.includes(dept.id)
    )

    if (selectedDepartmentsForCompany.length > 0 || specificCompanyIds.includes(company.id)) {
      return "specific"
    }

    if (selectedDepartmentsForCompany.length === 0) {
      return "all"
    } else {
      return "specific"
    }
  }

  return (
    <List sx={{ padding: 0, margin: 0, width: "100%" }}>
      <StyledListItem sx={{ pb: 2 }}>
        <StyledBoxWrapper>
          <Typography variant="h5">
            {t(`documents.companies_and_departments`, `Companies and departments`)}
          </Typography>
          {availabilityStatus}
        </StyledBoxWrapper>
        <Typography color="text.secondary">
          {t(
            "documents.availability_description",
            "Select which companies and departments should have access to the document."
          )}
        </Typography>
        <FormControlLabel
          sx={{ pt: 3 }}
          label={
            <Typography fontWeight={600}>
              {t(`documents.grant_access`, "Grant access to all companies and departments")}
            </Typography>
          }
          control={
            <Switch
              id="isAvailableForAll"
              checked={values.isAvailableForAll || false}
              onChange={(e) => handleAllCompaniesChange(e.target.checked)}
            />
          }
        />
      </StyledListItem>

      {customerCompanies.map((company: Company, index: number) => {
        const selectionType = getCompanySelectionType(company)
        const selectedDepartmentIds: number[] = values.departmentIds || []

        return (
          <React.Fragment key={company.id}>
            <StyledListItemWrapper>
              {company.departments.length > 0 ? (
                <StyleAccordion>
                  <StyleAccordionSummary
                    expandIcon={<FontAwesomeIcon icon={faChevronDown as IconProp} />}
                    aria-controls="settings">
                    <StyledFormControlLabelBold
                      label={company.name}
                      onClick={(event) => event.stopPropagation()}
                      control={
                        <Checkbox
                          id={`company-${company.id}`}
                          checked={selectionType !== "none"}
                          onChange={(_, checked) => {
                            if (!checked && selectionType !== "none") {
                              const wasAvailableForAll = values.isAvailableForAll
                              const baseCompanyIds: number[] = wasAvailableForAll
                                ? allCompanyIds
                                : values.companyIds || []
                              const baseDepartmentIds: number[] = wasAvailableForAll
                                ? []
                                : values.departmentIds || []

                              setFieldValue(
                                "companyIds",
                                baseCompanyIds.filter((x) => x !== company.id)
                              )
                              setFieldValue(
                                "departmentIds",
                                baseDepartmentIds.filter(
                                  (x) => !company.departments.map((d) => d.id).includes(x)
                                )
                              )
                              setFieldValue("isAvailableForAll", false)
                            } else if (checked) {
                              handleCompanyDepartmentSelection(company, "all")
                              setSpecificCompanyIds((previous) => previous.filter((id) => id !== company.id))
                            }
                          }}
                        />
                      }
                    />
                  </StyleAccordionSummary>
                  <StyleAccordionDetails>
                    <Box sx={{ pb: 3, px: 3 }}>
                      <ListItem sx={{ p: 0 }}>
                        <FormControlLabel
                          control={<Radio checked={selectionType === "all"} />}
                          onChange={() => handleCompanyDepartmentSelection(company, "all")}
                          label={
                            <Typography fontWeight={selectionType === "all" ? 600 : 400}>
                              {t("documents.all_departments", "All departments")}
                            </Typography>
                          }
                        />
                      </ListItem>

                      <Typography variant="bodyMedium" sx={{ mt: 1.5 }} fontWeight={600}>
                        {t("documents.specific_departments", "Specific departments")}
                      </Typography>
                      <Box
                        sx={{
                          pl: 1,
                          pr: 1,
                          ...(company.departments.length > 5 && {
                            maxHeight: 200,
                            overflowY: "auto"
                          })
                        }}>
                        {company.departments.map((department: Department) => {
                          return (
                            <ListItem key={department.id} sx={{ p: 0 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    id={`department-${department.id}`}
                                    checked={selectedDepartmentIds.includes(department.id)}
                                    onChange={() => {
                                      handleCompanyDepartmentSelection(company, "specific", department.id)
                                    }}
                                  />
                                }
                                label={department.name}
                              />
                            </ListItem>
                          )
                        })}
                      </Box>
                    </Box>
                  </StyleAccordionDetails>
                </StyleAccordion>
              ) : (
                <StyledFormControlLabelBold
                  sx={{ paddingLeft: theme.spacing(2) }}
                  label={company.name}
                  control={
                    <Checkbox
                      id={`company-${company.id}`}
                      checked={selectionType !== "none"}
                      onChange={(_, checked) => {
                        if (checked) {
                          const newCompanyIds = [...(values.companyIds || []), company.id]

                          if (!maybeCollapseToAvailableForAll(newCompanyIds, values.departmentIds || [])) {
                            setFieldValue("companyIds", newCompanyIds)
                            setFieldValue("isAvailableForAll", false)
                          }
                        } else {
                          const wasAvailableForAll = values.isAvailableForAll
                          const baseCompanyIds: number[] = wasAvailableForAll
                            ? allCompanyIds
                            : values.companyIds || []

                          setFieldValue(
                            "companyIds",
                            baseCompanyIds.filter((x) => x !== company.id)
                          )
                          setFieldValue("departmentIds", wasAvailableForAll ? [] : values.departmentIds || [])
                          setFieldValue("isAvailableForAll", false)
                        }
                      }}
                    />
                  }
                />
              )}
            </StyledListItemWrapper>
          </React.Fragment>
        )
      })}
    </List>
  )
}
